import { useState, createContext, useContext, useMemo, useCallback } from 'react';
import PortalIdParser from 'PortalIdParser';
import { captureException } from 'raven-js';
import { getFullUrl } from 'hubspot-url-utils';
import { useCreateStagedFormMutation, useGetStagedFormStatusQuery } from 'forms-ai-lib/__generated__/chirp/com/hubspot/forms/rpc/HubSpotFormsSkillsService.hooks';
import getCurrentBusinessUnit from 'forms-ai-lib/internal/utils/getCurrentBusinessUnit';
import { usePromoteStagedFormMutation } from 'forms-ai-lib/__generated__/chirp/com/hubspot/forms/rpc/HubSpotFormsStagingService.hooks';
import { trackEvent } from '../internal/utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
export const StagedFormsContext = /*#__PURE__*/createContext({
  prompt: '',
  setPrompt: () => {},
  handleCreateForm: async () => {},
  handleFormPersistence: async () => {},
  createdStagedFormLoading: false,
  promoteStagedFormLoading: false,
  executionLoading: false,
  stagedForm: null,
  shouldPoll: false,
  cancelCreation: () => {}
});
export const StagedFormsContextProvider = ({
  children
}) => {
  const [prompt, setPrompt] = useState('');
  const [shouldPoll, setShouldPoll] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [stagedForm, setStagedForm] = useState(null);
  const [createdStagedForm, {
    loading: createdStagedFormLoading
  }] = useCreateStagedFormMutation();
  const [promoteStagedForm, {
    loading: promoteStagedFormLoading
  }] = usePromoteStagedFormMutation();
  const {
    loading: executionLoading,
    stopPolling
  } = useGetStagedFormStatusQuery({
    variables: {
      executionId: executionId || ''
    },
    skip: !shouldPoll || executionId === null || executionId.length <= 0,
    pollInterval: 2500,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (data.result.state === 'COMPLETE' && data.result.stagedFormId) {
        setStagedForm(data.result);
        setExecutionId(null);
        setShouldPoll(false);
      }
    },
    onError: error => {
      setShouldPoll(false);
      captureException(error);
    }
  });
  const handleCreateForm = useCallback(async userInput => {
    setPrompt(userInput);
    try {
      var _resp$data;
      const resp = await createdStagedForm({
        variables: {
          businessUnitId: getCurrentBusinessUnit(),
          userInput
        }
      });
      setExecutionId(((_resp$data = resp.data) === null || _resp$data === void 0 ? void 0 : _resp$data.result.executionId) || null);
      setShouldPoll(true);
    } catch (error) {
      console.error(error);
      captureException(error);
    }
  }, [createdStagedForm, setPrompt, setExecutionId, setShouldPoll]);
  const handleFormPersistence = useCallback(async () => {
    if (stagedForm !== null && stagedForm !== void 0 && stagedForm.stagedFormId) {
      try {
        var _resp$data2, _resp$data3;
        const resp = await promoteStagedForm({
          variables: {
            stagedFormId: (stagedForm === null || stagedForm === void 0 ? void 0 : stagedForm.stagedFormId) || ''
          }
        });
        trackEvent('saveStagedForm', {
          stagedFormId: (stagedForm === null || stagedForm === void 0 ? void 0 : stagedForm.stagedFormId) || '',
          createdFormId: ((_resp$data2 = resp.data) === null || _resp$data2 === void 0 ? void 0 : _resp$data2.result.formId) || ''
        });
        if ((_resp$data3 = resp.data) !== null && _resp$data3 !== void 0 && _resp$data3.result.formId) {
          var _resp$data4;
          const newUrl = `${getFullUrl('app')}/forms/${PortalIdParser.get()}/new-editor/${(_resp$data4 = resp.data) === null || _resp$data4 === void 0 ? void 0 : _resp$data4.result.formId}?createdWithAi=true`;
          window.location.href = newUrl;
        }
      } catch (error) {
        console.error(error);
        trackEvent('saveStagedFormFailed', {
          stagedFormId: (stagedForm === null || stagedForm === void 0 ? void 0 : stagedForm.stagedFormId) || ''
        });
        captureException(error);
      }
    }
  }, [promoteStagedForm, stagedForm === null || stagedForm === void 0 ? void 0 : stagedForm.stagedFormId]);
  const cancelCreation = useCallback(() => {
    stopPolling();
    setExecutionId(null);
    setShouldPoll(false);
    setStagedForm(null);
  }, [stopPolling, setExecutionId, setShouldPoll, setStagedForm]);
  const value = useMemo(() => ({
    handleCreateForm,
    handleFormPersistence,
    createdStagedFormLoading,
    promoteStagedFormLoading,
    executionLoading,
    stagedForm,
    shouldPoll,
    cancelCreation,
    prompt,
    setPrompt
  }), [prompt, setPrompt, handleCreateForm, handleFormPersistence, createdStagedFormLoading, promoteStagedFormLoading, executionLoading, stagedForm, shouldPoll, cancelCreation]);
  return /*#__PURE__*/_jsx(StagedFormsContext.Provider, {
    value: value,
    children: children
  });
};
export const useStagedForms = () => {
  return useContext(StagedFormsContext);
};